import React from "react";
import type { VariantPrice } from "../../../types/ProductListing";

interface DesktopPriceUpdateViewProps {
  variants: VariantPrice[];
  onPriceChange: (variantId: string, newPrice: number) => void;
}

const DesktopPriceUpdateView: React.FC<DesktopPriceUpdateViewProps> = ({
  variants,
  onPriceChange,
}) => {
  const handlePriceInputChange = (variantId: string, value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      onPriceChange(variantId, numValue);
    } else if (value === "") {
      onPriceChange(variantId, 0);
    }
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "decimal",
      minimumFractionDigits: currency === "IDR" ? 0 : 2,
      maximumFractionDigits: currency === "IDR" ? 0 : 2,
    }).format(price);
  };

  const getCurrencySymbol = (currency: string) => {
    switch (currency.toLowerCase()) {
      case "usd":
        return "$";
      case "idr":
        return "Rp";
      case "sgd":
        return "S$";
      case "myr":
        return "RM";
      default:
        return currency;
    }
  };

  const getCurrencyPlaceholder = (currency: string) => {
    switch (currency.toLowerCase()) {
      case "usd":
        return "e.g. 3000";
      case "idr":
        return "e.g. 3000000";
      case "sgd":
        return "0.00";
      case "myr":
        return "0.00";
      default:
        return "0.00";
    }
  };

  if (variants.length === 0) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
        <div className="tw-text-center">
          <span className="material-symbols-outlined tw-text-4xl tw-text-gray-400 tw-mb-2">
            inventory_2
          </span>
          <p className="tw-text-gray-500">
            No variants available for price update
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="tw-overflow-x-auto">
        <table
          className="tw-min-w-full tw-divide-y tw-divide-gray-200"
          data-testid="price-table"
        >
          <thead className="tw-bg-gray-50" data-testid="price-table-header">
            <tr>
              <th
                className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-[25%]"
                scope="col"
              >
                Product / Variant
              </th>
              <th
                className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider"
                scope="col"
              >
                Current Price
              </th>
              <th
                className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider"
                scope="col"
              >
                Regular Price (
                {getCurrencySymbol(variants[0]?.currency || "IDR")})
              </th>
              <th
                className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider"
                scope="col"
              >
                Currency
              </th>
            </tr>
          </thead>
          <tbody
            className="tw-divide-y tw-divide-gray-200 tw-bg-white"
            data-testid="price-table-body"
          >
            {variants.map((variant, index) => (
              <tr key={variant.variantId} data-testid={`variant-row-${index}`}>
                <td className="tw-px-3 tw-py-3 tw-align-top">
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <img
                      className="tw-h-10 tw-w-10 tw-rounded-md tw-object-cover tw-shrink-0"
                      src={
                        variant.image ||
                        "https://via.placeholder.com/40x40?text=No+Image"
                      }
                      alt={variant.variantName}
                      data-testid={`variant-image-${index}`}
                    />
                    <div className="tw-flex tw-flex-col">
                      <p
                        className="tw-text-sm tw-font-medium tw-text-gray-800"
                        data-testid={`variant-name-${index}`}
                      >
                        {variant.variantName}
                      </p>
                      <p
                        className="tw-text-xs tw-text-gray-500"
                        data-testid={`variant-sku-${index}`}
                      >
                        Master SKU: {variant.masterSku}
                      </p>
                      <p
                        className="tw-text-xs tw-text-gray-500"
                        data-testid={`marketplace-sku-${index}`}
                      >
                        Marketplace SKU: {variant.marketplaceSku}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="tw-px-3 tw-py-3 tw-align-top tw-whitespace-nowrap">
                  <input
                    className="form-input tw-w-28 !tw-h-[38px] tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-primary focus:tw-ring-primary tw-text-sm tw-bg-gray-100 tw-text-gray-500"
                    readOnly
                    type="text"
                    value={formatPrice(variant.currentPrice, variant.currency)}
                    data-testid={`variant-current-price-${index}`}
                  />
                </td>
                <td className="tw-px-3 tw-py-3 tw-align-top tw-whitespace-nowrap">
                  <input
                    type="number"
                    min="0"
                    value={
                      variant.newPrice > 0 ? variant.newPrice.toString() : ""
                    }
                    onChange={(e) =>
                      handlePriceInputChange(variant.variantId, e.target.value)
                    }
                    placeholder={getCurrencyPlaceholder(variant.currency)}
                    className="form-input tw-w-28 !tw-h-[38px] tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-primary focus:tw-ring-primary tw-text-sm"
                    data-testid={`variant-new-price-${index}`}
                  />
                </td>
                <td
                  className="tw-px-3 tw-py-3 tw-align-top tw-whitespace-nowrap tw-text-sm tw-text-gray-500"
                  data-testid={`variant-currency-${index}`}
                >
                  {variant.currency}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default DesktopPriceUpdateView;
